import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const MIN = 12;

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [check, setCheck] = useState(null);   // null | "checking" | {ok, problem}

  // live policy feedback (length, name/email, reuse, known breaches) — debounced, never stored
  useEffect(() => {
    if (!open || next.length < MIN) { setCheck(null); return; }
    setCheck("checking");
    const t = setTimeout(() => {
      api.post("/auth/password-check", { password: next }).then(({ data }) => setCheck(data)).catch(() => setCheck(null));
    }, 500);
    return () => clearTimeout(t);
  }, [next, open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — other devices will need to sign in again");
      setCurrent("");
      setNext("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const tooShort = next.length > 0 && next.length < MIN;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#0A8CF2]" /> Change password
          </DialogTitle>
          <DialogDescription className="text-slate-400">At least {MIN} characters — a short phrase works well. We check it against known data breaches.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-300 mb-1 block">Current password</Label>
            <Input type="password" data-testid="current-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
          </div>
          <div>
            <Label className="text-slate-300 mb-1 block">New password</Label>
            <Input type="password" data-testid="new-password-input" value={next} onChange={(e) => setNext(e.target.value)} className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
            <p className="text-xs mt-1.5 min-h-[1rem]" data-testid="password-policy-hint">
              {tooShort && <span className="text-slate-500">{MIN - next.length} more character{MIN - next.length === 1 ? "" : "s"}</span>}
              {check === "checking" && <span className="text-slate-500 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Checking…</span>}
              {check && check !== "checking" && check.ok && <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Looks good</span>}
              {check && check !== "checking" && !check.ok && <span className="text-red-400 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" />{check.problem}</span>}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !current || next.length < MIN || (check && check !== "checking" && !check.ok)} data-testid="save-password-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
